import exportSmallTokens from '@amzn/meridian-tokens/base/icon/export-small';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Icon from '@amzn/meridian/icon';
import Input from '@amzn/meridian/input';
import Link from '@amzn/meridian/link';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Auth from '../../../Auth';
import Storage from '../../../Util/Storage';
import FeatureConfig from '../../../config/FeaturesConfig';
import { ProxyStorageKeys } from '../../../hooks/useProxy';
import { Profile } from '../../../Types/Profile';
import { getEnvConfigByKey } from '../../../config/EnvConfig';
import { StateContext } from '../../../GlobalStateStore';
import DataState from '../../../Types/DataState';

// For impersonation, Subject is provided as '<username>@<company>@<region>
const getUserName = (imprSubject: string): string => {
  return imprSubject.substring(0, imprSubject.indexOf('@'));
};

interface ImpersonateProps {
  profile: Profile;
  profileState: string;
}

// eslint-disable-next-line no-empty-pattern
const Impersonate: React.FC<ImpersonateProps> = ({
  profile,
  profileState,
}): React.ReactElement => {
  const intl = useIntl();
  const globalState = useContext(StateContext);
  const { inspireOptions } = globalState?.state;
  const { cscEntityPrefix } = inspireOptions.data;
  const [imprSubject, setImprSubject] = useState(
    Storage.getItem('imprSubject')
      ? (Storage.getItem('imprSubject') as string)
      : ''
  );
  const [imprUser, setImprUser] = useState(
    Storage.getItem('imprUser') ? (Storage.getItem('imprUser') as string) : ''
  );
  const [impersonating, setImpersonating] = useState(Auth.isImpersonating());
  const [imperUserName, setImperUserName] = useState(getUserName(imprSubject));
  const [openAdd, setOpenAdd] = useState(false);

  useEffect(() => {
    if (profileState === DataState.LOADED) {
      setImprSubject(
        Storage.getItem('imprSubject')
          ? (Storage.getItem('imprSubject') as string)
          : profile.user.alias
              .concat('@')
              .concat(profile.user.owner.toLowerCase())
              .concat('@')
              .concat(profile.user.homeRegion)
      );

      setImprUser(
        Storage.getItem('imprUser')
          ? (Storage.getItem('imprUser') as string)
          : `${cscEntityPrefix}:${profile.user.userID}`
      );
    }
  }, [profileState]);

  const regionName: string =
    getEnvConfigByKey('regionName') || process.env.REACT_APP_REGION || '';

  const onClickAddButton = useCallback(() => setOpenAdd(true), []);
  const onAddClose = useCallback(() => {
    setOpenAdd(false);
  }, []);

  const onImpersonate = (): void => {
    Storage.setItem('imprSubject', imprSubject);
    Storage.setItem('imprUser', imprUser);
    setImpersonating(true);
    setImperUserName(getUserName(imprSubject));
    setOpenAdd(false);
    FeatureConfig.resetConfig();
  };

  const onEndImpersonate = (): void => {
    Storage.setItem('imprSubject', '');
    Storage.setItem('imprUser', '');
    // Also clear any proxy session that might have been started while impersonating
    Storage.setItem(ProxyStorageKeys.ORIGINAL_USER_ID, '');
    Storage.setItem(ProxyStorageKeys.PROXY_USER_ID, '');
    setImpersonating(false);
    setOpenAdd(false);
    FeatureConfig.resetConfig();
  };

  // Input parameters for impersonation are invalid if
  // Subject does not confirm to '<username>@<company>@<region>' => at least 3 groups separated by '@'
  // User does not confirm to 'cscentral_<region>_user:<userid>' => we have a id after ':'
  const invalidImpersonateInput = (): boolean => {
    return (
      imprSubject.trim() === '' ||
      imprUser.trim() === '' ||
      imprSubject.split('@').length < 3 ||
      imprUser.indexOf(':') === -1 ||
      imprUser.indexOf(':') === imprUser.length - 1
    );
  };

  return (
    <Box>
      <Column spacing='300'>
        <Row>
          <Text type='h100'>
            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
            <Link onClick={onClickAddButton} type='secondary'>
              <Icon tokens={exportSmallTokens} />{' '}
              <FormattedMessage
                id={
                  impersonating
                    ? 'cs_perfportal_impersonate_user_link'
                    : 'cs_perfportal_impersonate_link'
                }
                defaultMessage={
                  impersonating ? 'Impersonating {alias}' : 'Impersonate'
                }
                values={{ alias: imperUserName }}
                description='Impersonate button'
              />
            </Link>
            <Modal
              title={intl.formatMessage({
                id: 'cs_perfportal_ImpersonateAnotherUser',
                defaultMessage: 'Impersonate another user',
              })}
              open={openAdd}
              scrollContainer='viewport'
              closeLabel={intl.formatMessage({
                id: 'cs_perfportal_popover_close',
                defaultMessage: 'close',
              })}
              aria-describedby='modal-description'
              width='350px'
              data-testid='impersonation-modal'
            >
              <Column spacing='200'>
                <Text>
                  <FormattedMessage
                    id='cs_perfportal_impersonation_subject'
                    defaultMessage='Subject:'
                  />
                </Text>
                <Input
                  value={imprSubject}
                  disabled={impersonating}
                  onChange={setImprSubject}
                  type='text'
                  placeholder='<username>@<company>@<region>'
                />
                <Text>
                  <FormattedMessage
                    id='cs_perfportal_impersonation_user'
                    defaultMessage='User:'
                  />
                </Text>
                <Input
                  value={imprUser}
                  disabled={impersonating}
                  onChange={setImprUser}
                  type='text'
                  placeholder='cscentral_<region>_user:<userid>'
                />
              </Column>
              <ModalFooter>
                <Row alignmentHorizontal='end' widths='fit'>
                  <Button type='secondary' size='small' onClick={onAddClose}>
                    <FormattedMessage
                      id='cs_perfportal_cancel'
                      defaultMessage='Cancel'
                    />
                  </Button>
                  {impersonating ? (
                    <Button
                      type='primary'
                      size='small'
                      onClick={onEndImpersonate}
                    >
                      <FormattedMessage
                        id='cs_perfportal_end_impersonate_button'
                        defaultMessage='End Impersonate'
                      />
                    </Button>
                  ) : (
                    <Button
                      type='primary'
                      size='small'
                      onClick={onImpersonate}
                      disabled={invalidImpersonateInput()}
                    >
                      <FormattedMessage
                        id='cs_perfportal_impersonate_button'
                        defaultMessage='Impersonate'
                      />
                    </Button>
                  )}
                </Row>
              </ModalFooter>
            </Modal>
          </Text>
        </Row>
      </Column>
    </Box>
  );
};

export default Impersonate;
